import type { CourseItem } from '../shared/types';
import { formatDateLabel, isSameDay, weekdayLabel } from '../shared/time';

interface ScheduleGridProps {
  dates: string[];
  courseMap: Record<string, CourseItem[]>;
  today: string;
  tomorrow: string;
  onCourseClick: (course: CourseItem) => void;
}

const SOURCE_LABEL: Record<string, string> = {
  baseline: '基线',
  override: '永久改',
  'temporary-modify': '临时改',
  'temporary-add': '临时加',
};

const SOURCE_COLOR: Record<string, string> = {
  baseline: 'text-slate-400',
  override: 'text-indigo-400',
  'temporary-modify': 'text-amber-500',
  'temporary-add': 'text-emerald-500',
};

export function ScheduleGrid({ dates, courseMap, today, tomorrow, onCourseClick }: ScheduleGridProps) {
  return (
    <div className="grid gap-2.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
      {dates.map((date) => {
        const courses = courseMap[date] ?? [];
        const isToday = isSameDay(date, today);
        const isTomorrow = isSameDay(date, tomorrow);

        return (
          <div
            key={date}
            className={[
              'rounded-2xl border p-3 transition-colors',
              isToday
                ? 'border-blue-200 bg-blue-50/60'
                : isTomorrow
                  ? 'border-amber-200 bg-amber-50/60'
                  : 'border-slate-200 bg-white',
            ].join(' ')}
          >
            {/* 列头 */}
            <div className="mb-2.5 flex items-baseline justify-between gap-1">
              <div className="flex items-baseline gap-1.5">
                <span className={[
                  'text-sm font-medium',
                  isToday ? 'text-blue-600' : isTomorrow ? 'text-amber-600' : 'text-slate-600',
                ].join(' ')}>
                  {weekdayLabel(date)}
                </span>
                <span className="text-[11px] text-slate-400">{formatDateLabel(date)}</span>
              </div>
              {courses.length > 0 && !isToday && !isTomorrow && (
                <span className="text-xs text-slate-400">{courses.length}</span>
              )}
            </div>

            {/* 课程列表 */}
            <div className="flex flex-col gap-1.5">
              {courses.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 py-3 text-center text-xs text-slate-300">
                  无课
                </div>
              ) : (
                courses.map((course) => (
                  <button
                    key={course.uid}
                    type="button"
                    onClick={() => onCourseClick(course)}
                    className="group w-full rounded-xl border border-slate-100 bg-white p-2.5 text-left shadow-sm transition hover:border-slate-200 hover:shadow-md active:scale-[0.99]"
                  >
                    <div className="flex items-start justify-between gap-1">
                      <span className="text-xs font-medium leading-snug text-slate-800 group-hover:text-blue-600 transition-colors">
                        {course.summary}
                      </span>
                      <span className={['shrink-0 text-[10px] mt-0.5', SOURCE_COLOR[course.source] ?? 'text-slate-400'].join(' ')}>
                        {SOURCE_LABEL[course.source] ?? course.source}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-400">{course.timeLabel}</div>
                    {course.location && (
                      <div className="mt-0.5 text-[11px] text-slate-400 truncate">{course.location}</div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
